import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { message, Popconfirm } from 'antd';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
// ux9：日期统一出口
import { fmtDateShort } from '../../lib/format';
import { useHasPermission } from '../../lib/permissions'; // P1-3 删客户按钮权限位
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  CustCell, Pill, Btn, AiBox, JourneyCta, StageDots, EmptyHint,
  STAGE_META, STAGE_KEYS, STATUS_META, avatarSlot,
} from '../../components/ui';
import { JourneyModal } from '../../components/JourneyModal';
import { NewCustomerRequestModal } from '../../components/NewCustomerRequestModal';
import { ScreenshotAllocationModal } from '../../components/ScreenshotAllocationModal';
import { useAuthStore } from '../../store/auth';
import { useDealWatcher } from '../../lib/hooks';

type Tab = 'all' | 'probation' | 'active' | 'dealt' | 'in_service' | 'pool' | 'deleted';

const TAB_TO_PARAM: Record<Tab, any> = {
  all: {},
  probation: { status: 'probation' },
  active: { status: 'active' },
  dealt: { status: 'dealt' },
  in_service: { status: 'in_service' },
  pool: { status: 'pool' },
  deleted: { status: 'deleted' }, // 大流程批7：被删客户(已退L0回公海)，仅admin/director可见此tab
};

const AVATAR_BG: Record<number, string> = {
  1: 'linear-gradient(135deg, #6294f5, #3370ff)',
  2: 'linear-gradient(135deg, #51cf8a, #34c759)',
  3: 'linear-gradient(135deg, #d6649e, #c44569)',
  4: 'linear-gradient(135deg, #f6c155, #f0a020)',
  5: 'linear-gradient(135deg, #9b7dd6, #7c4dff)',
  6: 'linear-gradient(135deg, #ee7065, #ee5253)',
  7: 'linear-gradient(135deg, #5eb6e0, #3aafda)',
  8: 'linear-gradient(135deg, #95cc6c, #76b639)',
};

function getFlag(c: any): { kind: 's' | 'a' | 'b' | 'c'; label?: string } | undefined {
  const h = Number(c.healthScore ?? 0);
  if (h >= 85) return { kind: 's' };
  if (h >= 70) return { kind: 'a' };
  if (h >= 50) return { kind: 'b' };
  if (h >= 30) return { kind: 'c' };
  return undefined;
}

// batch-fix#H3：来源英文键→中文 label(对齐归一后存英文)；列表来源筛选 + 单元格显示用。
const SOURCE_LABELS: Record<string, string> = {
  xiaohongshu: '小红书', douyin: '抖音', wechat_channel: '视频号', baidu: '百度推广',
  website: '官网', phone: '电话', self_traffic: '自然流量', fanke: '返客', pool: '公海', other: '其他',
};
const SOURCE_OPTS = Object.entries(SOURCE_LABELS).map(([value, label]) => ({ value, label }));
const srcLabel = (v?: string | null) => (v ? (SOURCE_LABELS[v] ?? v) : '—');

function wechatPill(status?: string) {
  if (!status || status === 'unknown') return <Pill tone="default">未加微信</Pill>;
  if (status === 'accepted' || status === 'approved') return <Pill tone="success">已通过</Pill>;
  if (status === 'added') return <Pill tone="success">已加微信</Pill>; // #6：跟进表单写的是 'added'，补映射防显英文原值
  if (status === 'pending') return <Pill tone="warning">等待中</Pill>;
  if (status === 'rejected') return <Pill tone="danger">被拒</Pill>;
  return <Pill tone="default">{status}</Pill>;
}

function money(v: any) {
  if (v === null || v === undefined || Number(v) === 0) return <div className="money muted">—</div>;
  return <div className="money">¥ {Number(v).toLocaleString()}</div>;
}

export function CustomersListPage() {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const role = useAuthStore((s) => s.user?.role);
  const canCreate = ['manager', 'director', 'admin'].includes(role ?? '');
  // 条7：工作台/团队/健康告警卡片下钻 → 列表按 URL 初始化筛选(原只读沙盒 banner 参数、忽略 status/owner/source/stale/maxHealth → 全显示、卡片数核不了)
  const [tab, setTab] = useState<Tab>(() => {
    const s = searchParams.get('status');
    return s && s in TAB_TO_PARAM ? (s as Tab) : 'all';
  });
  // ux4：page 也 lazy-init 自 URL（原写死 1），刷新/后退不丢页码
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get('page'));
    return p >= 1 ? p : 1;
  });
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [journeyCid, setJourneyCid] = useState<number | null>(null);
  // ux1：删客户触发钮防重——精确到行(actingId)，删除进行中禁用该行删除钮防重复打开二次确认
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const pageSize = 20;

  // C-4.5 P2 / C-5 banner — 沙盒 commit 跳转 或 撤销跳转过来（searchParams 已在顶部声明）
  const fromSandbox = searchParams.get('fromSandbox');
  const importedCount = searchParams.get('imported');
  const fromRollback = searchParams.get('fromRollback');
  const rolledBackCount = searchParams.get('rolledBack');
  const [importBanner, setImportBanner] = useState<{ kind: 'commit' | 'rollback'; jobId: string; count: string } | null>(null);
  useEffect(() => {
    if (fromSandbox && importedCount) {
      setImportBanner({ kind: 'commit', jobId: fromSandbox, count: importedCount });
      message.success(`沙盒数据已正式导入 · ${importedCount} 个新客户`);
    } else if (fromRollback && rolledBackCount) {
      setImportBanner({ kind: 'rollback', jobId: fromRollback, count: rolledBackCount });
      message.success(`已撤销导入 #${fromRollback} · ${rolledBackCount} 行客户软删除`);
    }
    if (fromSandbox || fromRollback) {
      const next = new URLSearchParams(searchParams);
      next.delete('fromSandbox'); next.delete('imported');
      next.delete('fromRollback'); next.delete('rolledBack');
      setSearchParams(next, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // P2-2: 可点击优先级排序。默认按 lastFollowupAt（后端默认），用户点"优先级"切换 sortBy=priority。
  const [sortBy, setSortBy] = useState<'default' | 'priority'>('default');
  // P6-2 需求类型 filter chip（条7：下钻 ?demandType= 初始化）
  const [demandFilter, setDemandFilter] = useState<string | null>(() => searchParams.get('demandType'));
  // 归属人筛选(快批-6)：仅 manager/director/admin 用(sales 看自己无需)。条7：Team 卡片传 ?owner=（DTO 用 ownerId，两者都认）
  const [ownerFilter, setOwnerFilter] = useState<number | null>(() => {
    const o = searchParams.get('ownerId') ?? searchParams.get('owner');
    return o ? Number(o) : null;
  });
  const [sourceFilter, setSourceFilter] = useState<string>(() => searchParams.get('source') ?? ''); // batch-fix#H3：来源筛选(条7 下钻初始化)
  // 条7：工作台「超 N 天未跟进」+ 健康告警「低健康度」下钻——无对应筛选 UI，靠 URL 注入 + 顶部可清除横幅
  const [staleDays, setStaleDays] = useState<number | null>(() => {
    const s = searchParams.get('stale'); return s ? Number(s) : null;
  });
  const [maxHealth, setMaxHealth] = useState<number | null>(() => {
    const m = searchParams.get('maxHealth'); return m != null && m !== '' ? Number(m) : null;
  });
  const isMgrView = role !== 'sales';
  const canSeeDeleted = role === 'admin' || role === 'director'; // 大流程批7：已删除tab仅admin/director(对sales隐藏防误解为可恢复)
  // P6: sales 申请创建客户 modal
  const [showReqModal, setShowReqModal] = useState(false);
  // P6: admin/manager 截图智能分配 modal
  const [showAllocModal, setShowAllocModal] = useState(false);
  // ux4：tab/page/owner 写回 URL，刷新/后退不丢（增量改、replace 不堆历史中间态，保留 source/stale 等其它键）
  const syncUrl = (next: { tab?: Tab; page?: number; owner?: number | null; demand?: string | null; source?: string }) => {
    const sp = new URLSearchParams(searchParams);
    const t = next.tab ?? tab;
    const pg = next.page ?? page;
    const o = next.owner !== undefined ? next.owner : ownerFilter;
    const d = next.demand !== undefined ? next.demand : demandFilter;        // UX 补漏：需求类型写回 URL
    const src = next.source !== undefined ? next.source : sourceFilter;      // UX 补漏：来源写回 URL
    if (t === 'all') sp.delete('status'); else sp.set('status', t);
    if (pg <= 1) sp.delete('page'); else sp.set('page', String(pg));
    // 读侧兼容 ownerId||owner，写侧统一写 ownerId、清掉旧 owner 键避免双源
    sp.delete('owner');
    if (o == null) sp.delete('ownerId'); else sp.set('ownerId', String(o));
    if (!d) sp.delete('demandType'); else sp.set('demandType', d);
    if (!src) sp.delete('source'); else sp.set('source', src);
    setSearchParams(sp, { replace: true });
  };
  const params = useMemo(() => {
    const p: any = { page, pageSize, ...TAB_TO_PARAM[tab] };
    if (sortBy === 'priority') { p.sortBy = 'priority'; p.sortDir = 'desc'; }
    if (demandFilter) p.demandType = demandFilter;
    if (ownerFilter) p.ownerId = ownerFilter;
    if (sourceFilter) p.source = sourceFilter;
    if (staleDays) p.staleDays = staleDays;          // 条7：超 N 天未跟进
    if (maxHealth != null) p.maxHealth = maxHealth;  // 条7：低健康度下钻
    return p;
  }, [page, tab, pageSize, sortBy, demandFilter, ownerFilter, sourceFilter, staleDays, maxHealth]);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['customers', params],
    queryFn: () => $.get<{ items: any[]; total: number }>('/customers', params),
  });
  const canDelete = useHasPermission('customer:delete'); // P1-3：仅 admin/director(seed 0027 收回 manager)显删除
  // 归属人筛选下拉数据源（manager/director/admin 有 user:view；sales 不拉）
  const { data: ownerUsers } = useQuery({
    queryKey: ['owner-filter-users'],
    enabled: isMgrView,
    queryFn: () => $.get<{ items: any[] }>('/users', { pageSize: 200 }),
    staleTime: 300_000,
  });

  const { data: summary } = useQuery({
    queryKey: ['customers-summary'],
    queryFn: async () => {
      // v1.5 真修：补 in_service + pool 两个 status，让 4 tab 之和 = 全部
      const [all, prob, act, dealt, inService, pool, deleted] = await Promise.all([
        $.get<any>('/customers', { page: 1, pageSize: 1 }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'probation' }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'active' }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'dealt' }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'in_service' }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'pool' }),
        $.get<any>('/customers', { page: 1, pageSize: 1, status: 'deleted' }),
      ]);
      return {
        all: all.total,
        probation: prob.total, active: act.total,
        dealt: dealt.total, in_service: inService.total, pool: pool.total,
        deleted: deleted.total,
      };
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const dealtRev = items
    .filter((c: any) => c.status === 'dealt')
    .reduce((s: number, c: any) => s + Number(c.totalPaid ?? 0), 0);

  /** Fires confetti + gold toast when a customer transitions to dealt; returns
   *  the set of currently-glowing customer ids so we can mark the row gold. */
  const glowingIds = useDealWatcher(items);


  // Auto-select first row when the page loads / tab changes
  const visibleSelected = items.find((c: any) => c.id === selectedId);
  const effectiveSelectedId = visibleSelected ? selectedId : items[0]?.id ?? null;

  return (
    <>
      <PageHeader
        breadcrumb={['客户流程', '客户管理']}
        title="客户管理"
        tag="第 1 步"
        subtitle="所有客户的总入口"
        actions={
          <>
            <Btn kind="text">导出</Btn>
            {canCreate ? (
              <>
                <Btn kind="default" onClick={() => setShowAllocModal(true)}>🎯 智能分配</Btn>
                <Btn kind="primary" onClick={() => window.dispatchEvent(new CustomEvent('huanke:open-new-customer'))}>新增客户</Btn>
              </>
            ) : (
              <Btn kind="default" onClick={() => setShowReqModal(true)}>申请新客户</Btn>
            )}
          </>
        }
      />
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <div className="work-content" style={{ flex: 1 }}>
          {importBanner && (
            <div
              style={{
                marginBottom: 12, padding: '12px 16px', borderRadius: 6,
                background: importBanner.kind === 'rollback'
                  ? 'var(--warning-bg, #fff7e6)'
                  : 'var(--success-bg, #f6ffed)',
                border: `1px solid ${importBanner.kind === 'rollback' ? 'var(--warning, #ffd591)' : 'var(--success, #b7eb8f)'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20 }}>{importBanner.kind === 'rollback' ? '↩️' : '✅'}</span>
                <div>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>
                    {importBanner.kind === 'rollback'
                      ? `已撤销导入 #${importBanner.jobId} · ${importBanner.count} 行客户软删除`
                      : `沙盒数据已正式导入 · 新增 ${importBanner.count} 个客户`}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>
                    {importBanner.kind === 'rollback'
                      ? '软删除保留 30 天，期间可联系管理员恢复'
                      : `来源：沙盒任务 #${importBanner.jobId} · 30 天内可在导入历史撤销`}
                  </div>
                </div>
              </div>
              <Btn kind="text" onClick={() => setImportBanner(null)}>关闭</Btn>
            </div>
          )}
          {(staleDays || maxHealth != null) && (
            <div style={{
              marginBottom: 12, padding: '10px 14px', borderRadius: 6,
              background: 'var(--primary-bg, #eef3ff)', border: '1px solid var(--primary, #adc6ff)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <span style={{ fontSize: 13 }}>
                🔎 已按工作台下钻筛选：
                {staleDays ? `超 ${staleDays} 天未跟进` : ''}
                {staleDays && maxHealth != null ? ' · ' : ''}
                {maxHealth != null ? `健康度 ≤ ${maxHealth}` : ''}
              </span>
              <Btn kind="text" onClick={() => {
                setStaleDays(null); setMaxHealth(null); setPage(1);
                const next = new URLSearchParams(searchParams);
                next.delete('stale'); next.delete('maxHealth');
                setSearchParams(next, { replace: true });
              }}>清除筛选</Btn>
            </div>
          )}
          <StatsRow>
            <StatCard
              label="全部客户"
              value={summary?.all ?? '—'}
              unit="人"
              meta="较上周"
            />
            <StatCard
              label="试用期"
              value={summary?.probation ?? 0}
              unit="人"
              valueColor="warning"
              meta="新分配未首跟"
            />
            <StatCard
              label="正式持有"
              value={summary?.active ?? 0}
              unit="人"
              meta="活跃跟进中"
            />
            <StatCard
              label="已成交"
              value={summary?.dealt ?? 0}
              unit="人"
              valueColor="success"
              meta={dealtRev > 0 ? `¥ ${Math.round(dealtRev).toLocaleString()}` : '本月新增'}
            />
          </StatsRow>

          {tab === 'all' && summary?.all === 0 && (
            <EmptyHint kbd="N">
              <strong>开始使用 ›</strong>{' '}
              {canCreate ? '点击右上角"+"录入第一个客户，看他走完整个销售流程' : '联系主管，请他给你分配第一个客户'}
            </EmptyHint>
          )}

          <FilterBar
            active={tab}
            onChange={(k) => {
              setTab(k as Tab); setPage(1);
              // 条7 评审：显式点 tab = 退出工作台下钻视图——清掉 stale/maxHealth(否则 status=本tab AND status IN(active,probation) 矛盾→静默空列表、横幅还在误导)
              const clearDrill = staleDays || maxHealth != null;
              if (clearDrill) { setStaleDays(null); setMaxHealth(null); }
              // ux4：tab/page 写回 URL；顺带在同一次写入里清掉下钻参数(stale/maxHealth)，避免两次 setSearchParams 互相覆盖
              const sp = new URLSearchParams(searchParams);
              const t = k as Tab;
              if (t === 'all') sp.delete('status'); else sp.set('status', t);
              sp.delete('page'); // setPage(1) → 第1页省略 page 键
              if (clearDrill) { sp.delete('stale'); sp.delete('maxHealth'); }
              setSearchParams(sp, { replace: true });
            }}
            tabs={[
              { key: 'all', label: '全部', count: summary?.all },
              { key: 'probation', label: '试用期', count: summary?.probation },
              { key: 'active', label: '正式持有', count: summary?.active },
              { key: 'dealt', label: '已成交', count: summary?.dealt },
              // v1.5 真修：加"服务中"tab 收纳 status='in_service' 客户（之前漏 → 4 tab 之和 12 ≠ 全部 13）
              { key: 'in_service', label: '服务中', count: (summary as any)?.in_service },
              { key: 'pool', label: '公海池', count: (summary as any)?.pool },
              ...(canSeeDeleted ? [{ key: 'deleted', label: '已删除', count: (summary as any)?.deleted }] : []),
            ]}
          />
          {/* P6-2 需求类型 chip 行 */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>需求类型：</span>
            {[
              { v: null, label: '全部', icon: '' },
              { v: 'work_cert', label: '工作考证', icon: '🎯' },
              { v: 'fulltime_shop', label: '全职做店铺', icon: '🏪' },
              { v: 'sideline_shop', label: '副业做店铺', icon: '💰' },
              { v: 'brand_overseas', label: '品牌出海', icon: '🌍' },
            ].map((opt: any) => (
              <button key={opt.v ?? 'all'}
                      className={`filter-chip ${demandFilter === opt.v ? 'active' : ''}`}
                      onClick={() => { setDemandFilter(opt.v); setPage(1); syncUrl({ demand: opt.v, page: 1 }); }}
                      style={demandFilter === opt.v ? { background: 'var(--primary-bg)', borderColor: 'var(--primary)', color: 'var(--primary)' } : {}}>
                {opt.icon} {opt.label}
              </button>
            ))}
          </div>
          {isMgrView && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: 'var(--text-2)' }}>归属人：</span>
              <select
                value={ownerFilter ?? ''}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : null;
                  setOwnerFilter(v); setPage(1);
                  syncUrl({ owner: v, page: 1 }); // ux4：归属人 + 重置页码写回 URL
                }}
                style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
              >
                <option value="">全部归属人</option>
                {(ownerUsers?.items ?? []).map((u: any) => (
                  <option key={u.id} value={u.id}>{u.realName ?? u.username ?? `#${u.id}`}</option>
                ))}
              </select>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>来源：</span>
            <select
              value={sourceFilter}
              onChange={(e) => { setSourceFilter(e.target.value); setPage(1); syncUrl({ source: e.target.value, page: 1 }); }}
              style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
            >
              <option value="">全部来源</option>
              {SOURCE_OPTS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </div>
          <TableWrap>
            <table className="ck-table">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>客户</th>
                  <th style={{ width: '10%' }}>微信状态</th>
                  <th style={{ width: '9%' }}>来源</th>
                  <th style={{ width: '9%' }}>归属人</th>
                  <th
                    style={{ width: '14%', cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setSortBy((s) => s === 'priority' ? 'default' : 'priority')}
                    title="点击按 AI 优先级排序"
                  >
                    当前阶段 / 优先级 {sortBy === 'priority' && <span style={{ color: 'var(--purple)' }}>↓</span>}
                  </th>
                  <th style={{ width: '13%' }}>已收款</th>
                  <th style={{ width: '11%' }}>状态机</th>
                  <th style={{ width: '11%' }}>创建时间</th>
                  <th style={{ width: '12%', textAlign: 'right' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {isLoading && (
                  <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
                )}
                {!isLoading && items.length === 0 && (
                  <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无客户</td></tr>
                )}
                {items.map((c: any, idx: number) => {
                  // v1.5 P0 C-2.7 修：status=pool 时 stage 显示"已回收"（而非保留 L4/L5b 等原阶段，避免误导）
                  const stage = c.status === 'pool'
                    ? { tone: 'default' as const, label: '已回收' }
                    : (STAGE_META[c.currentStage] ?? { tone: 'default' as const, label: c.currentStage });
                  const status = STATUS_META[c.status] ?? { tone: 'default', label: c.status };
                  const flag = getFlag(c);
                  // First dealt customer in the "all" view acts as the journey示例 row
                  const isJourneyRow = idx === 0 && c.status === 'dealt';
                  const isGlowing = glowingIds.has(c.id);
                  const className = [
                    isJourneyRow ? 'journey-row' : '',
                    effectiveSelectedId === c.id ? 'selected' : '',
                    isGlowing ? 'hk-row-deal' : '',
                    // 新2(batch-7)：已成交客户行常驻渐变高亮(口径 status==='dealt'，不用 L7 脏数据)
                    c.status === 'dealt' ? 'hk-row-dealt' : '',
                  ].filter(Boolean).join(' ');
                  return (
                    <tr key={c.id} className={className} onClick={() => setSelectedId(c.id)}>
                      <td>
                        <CustCell
                          id={c.id}
                          name={c.nickname}
                          flag={isJourneyRow ? 'journey' : flag?.kind}
                          flagLabel={isJourneyRow ? '旅程范例' : undefined}
                          meta={c.internalCode || '—'}
                        />
                      </td>
                      <td>{wechatPill(c.wechatStatus)}</td>
                      <td><span className="time-text">{srcLabel(c.source)}</span></td>
                      <td><span className="time-text">{c.ownerName ?? '—'}</span></td>
                      <td>
                        <Pill tone={stage.tone}>{stage.label}</Pill>
                        {/* 软改硬(inc4)：L3+ 推进待主管审核 */}
                        {c.pendingStage && c.status !== 'pool' && (
                          <Pill tone="warning">⏳→{STAGE_META[c.pendingStage]?.label ?? c.pendingStage}</Pill>
                        )}
                        {/* P2-2 优先级评分（0-100，每天 08:00 cron 重算）
                            v2 阈值：≥60 红 / ≥40 橙 / 其余灰（v1 算法过严，99% 灰） */}
                        {c.priorityScore !== null && c.priorityScore !== undefined && (
                          <Pill tone={
                            Number(c.priorityScore) >= 60 ? 'danger' :
                            Number(c.priorityScore) >= 40 ? 'warning' : 'default'
                          }>
                            P{Math.round(Number(c.priorityScore))}
                          </Pill>
                        )}
                        <StageDots current={c.currentStage} />
                      </td>
                      <td>{money(c.totalPaid)}</td>
                      <td><Pill tone={status.tone}>{status.label}</Pill></td>
                      <td>
                        <div className="time-text">{fmtDateShort(c.createdAt)}</div>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="row-actions" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          {c.attachmentCount > 0 && (
                            <span
                              title={`${c.attachmentCount} 张截图`}
                              style={{
                                fontSize: 11, color: 'var(--text-2)',
                                background: 'var(--bg)', borderRadius: 10,
                                padding: '2px 7px', whiteSpace: 'nowrap',
                              }}
                            >
                              📷 {c.attachmentCount}
                            </span>
                          )}
                          {isJourneyRow ? (
                            <button className="row-action purple" onClick={(e) => { e.stopPropagation(); setJourneyCid(c.id); }}>查看旅程</button>
                          ) : (
                            <button className="row-action" onClick={(e) => { e.stopPropagation(); nav(`/customers/${c.id}`); }}>查看</button>
                          )}
                          {canDelete && (
                            <Popconfirm
                              title="确认删除该客户？"
                              description="名下有合同/订单/收款则不可删除"
                              okText="删除" cancelText="取消"
                              onConfirm={async () => {
                                // ux1：onConfirm 返回 Promise→AntD 给 OK 钮 spinner(隐式防护)；deletingId 再禁触发钮防重复打开
                                setDeletingId(c.id);
                                try { await $.delete(`/customers/${c.id}`); message.success('已删除'); refetch(); }
                                catch (err: any) { message.error(err?.response?.data?.message ?? '删除失败'); }
                                finally { setDeletingId(null); }
                              }}
                            >
                              {/* P1-3：删客户(软删)，仅 admin/director 显；行点击冒泡需拦 */}
                              <button className="row-action" style={{ color: '#d4380d' }}
                                      disabled={deletingId === c.id}
                                      onClick={(e) => e.stopPropagation()}>删除</button>
                            </Popconfirm>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {total > 0 && (
              <TableFooter total={total} page={page} pageSize={pageSize} onPage={(p) => { setPage(p); syncUrl({ page: p }); }} />
            )}
          </TableWrap>
        </div>

        <CustomerDetailPanel
          customerId={effectiveSelectedId}
          onOpenJourney={(id) => setJourneyCid(id)}
          onNavigate={(id) => nav(`/customers/${id}`)}
        />
      </div>

      <JourneyModal open={!!journeyCid} customerId={journeyCid} onClose={() => setJourneyCid(null)} />
      <NewCustomerRequestModal
        open={showReqModal}
        onClose={() => setShowReqModal(false)}
        onDone={() => setShowReqModal(false)}
      />
      <ScreenshotAllocationModal
        open={showAllocModal}
        onClose={() => setShowAllocModal(false)}
        onDone={(customerId) => {
          setShowAllocModal(false);
          nav(`/customers/${customerId}`);
        }}
      />
      {/* NewCustomerModal is mounted globally in Layout.tsx (so N shortcut works from any page).
          Listen for the event here only to refresh selected customer when one is created. */}
    </>
  );
}

/* Right 360px detail panel — shared shape used in scene-s1..s8 of v7 demo */
function CustomerDetailPanel({
  customerId, onOpenJourney, onNavigate,
}: {
  customerId: number | null;
  onOpenJourney: (id: number) => void;
  onNavigate: (id: number) => void;
}) {
  const { data: c } = useQuery({
    queryKey: ['customer', String(customerId)],
    queryFn: () => $.get<any>(`/customers/${customerId}`),
    enabled: !!customerId,
  });

  if (!customerId) return null;
  if (!c) {
    return (
      <aside className="detail-panel">
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</div>
      </aside>
    );
  }

  const slot = avatarSlot(c.id);
  const flag = getFlag(c);
  const stage = STAGE_META[c.currentStage] ?? { tone: 'default' as const, label: c.currentStage };
  const trackingDays = c.firstFollowupAt
    ? dayjs().diff(dayjs(c.firstFollowupAt), 'day')
    : (c.createdAt ? dayjs().diff(dayjs(c.createdAt), 'day') : 0);

  return (
    <aside className="detail-panel">
      <div className="detail-header">
        <div className="detail-avatar" style={{ background: AVATAR_BG[slot] }}>
          {c.nickname?.charAt(0)}
        </div>
        <div className="detail-name-block">
          <div className="detail-name">
            {c.nickname}
            {flag && <span className={`cust-name-flag flag-${flag.kind}`}>{flag.kind.toUpperCase()}</span>}
          </div>
          <div className="detail-sub">
            {[c.age && `${c.age} 岁`, c.region, c.source && srcLabel(c.source)].filter(Boolean).join(' · ')}
            {trackingDays > 0 && ` · 已跟 ${trackingDays} 天`}
          </div>
        </div>
      </div>
      <div className="detail-tabs">
        <button className="detail-tab active">详情</button>
        <button className="detail-tab" onClick={() => onNavigate(c.id)}>跟进历史</button>
        <button className="detail-tab" onClick={() => onNavigate(c.id)}>合同订单</button>
      </div>
      <div className="detail-body">
        <div className="detail-section">
          <JourneyCta
            title="查看完整旅程"
            sub="从触达到成交的全过程"
            onClick={() => onOpenJourney(c.id)}
          />
        </div>
        {c.aiProfileJson?.summary && (
          <div className="detail-section">
            <AiBox title="AI 建议">{c.aiProfileJson.summary}</AiBox>
          </div>
        )}
        <div className="detail-section">
          <div className="detail-section-title">
            <span>基本信息</span>
            <span className="detail-section-action" onClick={() => onNavigate(c.id)}>编辑</span>
          </div>
          <div className="detail-kv">
            <span className="detail-kv-label">微信</span>
            <span className="detail-kv-value">{c.wechatId ?? '—'}{c.wechatStatus === 'accepted' ? ' · 已通过' : ''}</span>
            <span className="detail-kv-label">手机</span>
            <span className="detail-kv-value">{c.phone ?? '—'}</span>
            <span className="detail-kv-label">学历</span>
            <span className="detail-kv-value">{c.education ?? '—'}</span>
            <span className="detail-kv-label">需求</span>
            <span className="detail-kv-value">{c.intent ?? '—'}</span>
            <span className="detail-kv-label">报价</span>
            <span className="detail-kv-value">{Number(c.totalDue) > 0 ? `¥ ${Number(c.totalDue).toLocaleString()}` : '—'}</span>
            <span className="detail-kv-label">成单预测</span>
            <span className="detail-kv-value" style={{ color: 'var(--success-text)' }}>
              {c.dealProbability ?? 0}%
            </span>
          </div>
        </div>
        <div className="detail-section">
          <div className="detail-section-title">
            <span>跟进进度</span>
            <Link to={`/customers/${c.id}`} className="detail-section-action">查看全部</Link>
          </div>
          <div className="timeline">
            {STAGE_KEYS.filter((k) => k !== 'L0').map((st) => {
              // inc6：旅程时间轴用统一字典（L1-L8 含 L5a/L5b 拆开，去除杂散 'L5'——STAGE_META['L5'] 已不存在会渲染空标签）；
              //   L0 为加微前置态、不入跟进时间轴。
              const reachedIdx = STAGE_KEYS.indexOf(c.currentStage);
              const stIdx = STAGE_KEYS.indexOf(st);
              const isCurrent = stIdx === reachedIdx;
              const isDone = reachedIdx >= 0 && stIdx >= 0 && stIdx < reachedIdx;
              return (
                <div key={st} className="tl-item">
                  <span className={`tl-dot ${isCurrent ? 'current' : isDone ? 'done' : ''}`} />
                  <div className="tl-stage">
                    <span
                      className="tl-stage-name"
                      style={{
                        color: isCurrent ? 'var(--primary)'
                          : isDone ? 'var(--text-1)'
                          : 'var(--text-3)',
                      }}
                    >
                      {STAGE_META[st]?.label}{isCurrent ? '（当前）' : !isDone ? ' · 待开始' : ''}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="detail-actions">
        <Btn kind="default" onClick={() => onNavigate(c.id)}>发起跟进</Btn>
        <Btn kind="primary" onClick={() => onOpenJourney(c.id)}>查看旅程</Btn>
      </div>
    </aside>
  );
}
